<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { ProvisionResult } from 'src/components/Camera/helpers/encryption/types/master-key';
	import { camera_setup_prompt } from 'src/components/Camera/stores/camera_setup_prompt';
	import { provisionMasterKey } from 'src/components/Camera/helpers/encryption/master-key-provisioning';
	import { PBKDF2_ITERATIONS } from 'src/components/Camera/helpers/encryption/master-key-management';
	import { base64ToBytes, bytesToBase64 } from 'src/components/Camera/helpers/encryption/encoding';
	import { real_id } from 'src/helpers/interface_helpers';
	import { copy } from 'src/helpers/copy_text';
	import {
		Alert,
		ButtonV2,
		CircleAvatar,
		IconButton,
		Input,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';

	export let device: Device;

	/** Minimum passphrase length enforced before provisioning a key. */
	const MIN_PASSPHRASE_LENGTH = 8;

	let passphrase = '';
	let confirmPassphrase = '';
	let passphraseError = '';
	let provisionLoading = false;

	let provisionResult: ProvisionResult | null = null;
	let showKeyModal = false;
	let showDebug = false;

	$: deviceId = real_id(device?.id);
	$: promptState = $camera_setup_prompt;
	// Only this view's device should react to the shared trigger store.
	$: showPassphraseModal = promptState.open && !!deviceId && promptState.deviceId === deviceId;

	// Dev-only diagnostics: the salt rides in the first 16 bytes of the blob
	// (salt[16] || iv[12] || ciphertext+tag) and iterations is a fixed constant.
	$: provisionDebugRows = provisionResult
		? [
				{
					label: 'Password key (PBKDF2-HMAC-SHA256, decryption key)',
					value: provisionResult.passwordKeyHex,
				},
				{
					label: 'PBKDF2 salt (base64)',
					value: bytesToBase64(base64ToBytes(provisionResult.blob).slice(0, 16)),
				},
				{ label: 'PBKDF2 iterations', value: String(PBKDF2_ITERATIONS) },
				{ label: 'Encrypted master key (blob)', value: provisionResult.blob },
				{ label: 'HMAC of master key', value: provisionResult.hmac },
			]
		: [];

	const resetPassphraseFields = () => {
		passphrase = '';
		confirmPassphrase = '';
		passphraseError = '';
	};

	const clearPassphraseError = () => {
		passphraseError = '';
	};

	const closePassphraseModal = () => {
		camera_setup_prompt.close();
		resetPassphraseFields();
	};

	const showKeyErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('camera_cloud_generate_key_error', {
				default: 'Could not generate an encryption key. Please retry.',
			}),
		});
	};

	const showKeySuccessToast = () => {
		toast.open({
			type: 'success',
			title: $format('camera_setup_success_toast', {
				default: 'Cloud recordings are set up.',
			}),
		});
	};

	const onSubmitPassphrase = async () => {
		if (passphrase.length < MIN_PASSPHRASE_LENGTH) {
			passphraseError = $format('camera_cloud_passphrase_too_short', {
				values: { count: MIN_PASSPHRASE_LENGTH },
				default: `The password must be at least ${MIN_PASSPHRASE_LENGTH} characters.`,
			});
			return;
		}
		if (passphrase !== confirmPassphrase) {
			passphraseError = $format('camera_cloud_passphrase_mismatch', {
				default: 'The passwords do not match.',
			});
			return;
		}

		provisionLoading = true;
		try {
			// Provisioning generates a fresh master_key on the camera and APPENDS a
			// new backup to this device's history, so a rotation leaves older
			// recordings decryptable via their original (older) backup + password.
			provisionResult = await provisionMasterKey(device, passphrase);
			camera_setup_prompt.close();
			resetPassphraseFields();
			showDebug = false;
			showKeyModal = true;
			showKeySuccessToast();
		} catch (error) {
			console.log('Failed to provision encryption key', error);
			showKeyErrorToast();
		}
		provisionLoading = false;
	};

	const onCopyMasterKey = () => {
		if (!provisionResult) return;
		copy(
			provisionResult.masterKey,
			$format('camera_cloud_master_key_copied', { default: 'Master key copied' }),
		);
	};

	const onCopyValue = (value: string, label: string) => {
		copy(value, `${label} copied`);
	};
</script>

<Modal
	showModal={showPassphraseModal}
	title={$format('camera_setup_modal_title', { default: 'Setup Cloud Recordings' })}
	backdropClose={false}
	on:close={closePassphraseModal}
>
	<svelte:fragment slot="content">
		<div class="d-flex justify-content-center mb-m">
			<CircleAvatar
				size="xxl"
				icon="fa-thin fa-clapperboard-play"
				borderStatus={{ icon: 'fa-solid fa-lock', color: 'color-primary' }}
			/>
		</div>
		<Text class="mb-s text-center">
			{$format('camera_setup_modal_message', {
				default:
					'Set a password to enable cloud recordings. Your videos, images and thumbnails are encrypted so only you can see them.',
			})}
		</Text>
		<Text styleType="body-small" class="mb-m text-center text-muted">
			{$format('camera_setup_modal_note', {
				default:
					"Recordings are not kept before setup. Only your password can decrypt them later - keep it safe, it can't be recovered.",
			})}
		</Text>
		<Text styleType="body" class="mb-s">
			{$format('camera_cloud_passphrase_section_title', { default: 'Set your password' })}
		</Text>
		<Input
			id="camera-cloud-passphrase"
			aria-label={$format('camera_cloud_passphrase_label', { default: 'Password' })}
			bind:value={passphrase}
			on:input={clearPassphraseError}
			size="l"
			type="password"
			class="width-full mb-s"
			label={$format('camera_cloud_passphrase_label', { default: 'Password' })}
			placeholder={$format('camera_cloud_passphrase_placeholder', {
				default: 'Enter password',
			})}
			a11yProps={{
				iconButtonAriaLabel: $format('button_show_password', { default: 'Show password' }),
			}}
		/>
		<Input
			id="camera-cloud-passphrase-confirm"
			aria-label={$format('camera_cloud_passphrase_confirm_label', {
				default: 'Confirm password',
			})}
			bind:value={confirmPassphrase}
			on:input={clearPassphraseError}
			error={passphraseError}
			size="l"
			type="password"
			class="width-full"
			label={$format('camera_cloud_passphrase_confirm_label', { default: 'Confirm password' })}
			placeholder={$format('camera_cloud_passphrase_confirm_placeholder', {
				default: 'Re-enter password',
			})}
			a11yProps={{
				iconButtonAriaLabel: $format('button_show_password', { default: 'Show password' }),
			}}
		/>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={provisionLoading}
			on:click={closePassphraseModal}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="primary"
			loading={provisionLoading}
			disabled={!passphrase || !confirmPassphrase || provisionLoading}
			on:click={onSubmitPassphrase}
			aria-label={$format('camera_setup_submit', { default: 'Complete setup' })}
		>
			{$format('camera_setup_submit', { default: 'Complete setup' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showKeyModal}
	title={$format('camera_setup_done_title', {
		default: 'Cloud recordings enabled',
	})}
	on:close={() => (showKeyModal = false)}
>
	<svelte:fragment slot="content">
		<div class="d-flex justify-content-center mb-l">
			<CircleAvatar size="xxl" icon="fas fa-shield-check text-color-success" />
		</div>
		<Text styleType="body-small" class="mb-m">
			{$format('camera_cloud_key_access_note', {
				default: 'Your recordings are now encrypted, and only your password can unlock them.',
			})}
		</Text>
		{#if provisionResult && import.meta.env.DEV}
			<Alert
				type="warning"
				class="mb-s"
				isHeadingBold
				heading="Dev-only"
				message="The encryption key below is printed in development builds only and is never shown to end users."
			/>
			<Text styleType="label-large" class="mb-xs">
				{$format('camera_cloud_key_backup_title', { default: 'Your encryption key' })}
			</Text>
			<Text styleType="body-small" class="mb-s">
				{$format('camera_cloud_key_backup_note', {
					default:
						'Save this key somewhere safe to recover access if you forget your password.',
				})}
			</Text>
			<div class="d-flex align-items-end" style="gap: var(--spacing-xs); min-width: 0;">
				<Input
					id="camera-cloud-master-key"
					class="width-full"
					size="l"
					type="password"
					readonly
					value={provisionResult.masterKey}
					aria-label={$format('camera_cloud_master_key_label', { default: 'Master key' })}
					a11yProps={{
						iconButtonAriaLabel: $format('button_show_password', {
							default: 'Show password',
						}),
					}}
				/>
				<IconButton
					icon="fas fa-copy"
					on:click={onCopyMasterKey}
					aria-label={$format('button_copy_to_clipboard', {
						default: 'Copy to clipboard',
					})}
				/>
			</div>

			<div class="mt-m">
				<ButtonV2
					size="s"
					variant="secondary"
					on:click={() => (showDebug = !showDebug)}
					aria-label={$format('camera_cloud_debug_details', {
						default: 'Technical details (dev only)',
					})}
				>
					{$format('camera_cloud_debug_details', {
						default: 'Technical details (dev only)',
					})}
				</ButtonV2>
				{#if showDebug}
					<div
						class="d-flex mt-s"
						style="flex-direction: column; gap: var(--spacing-s); width: 100%;"
					>
						{#each provisionDebugRows as row (row.label)}
							<div class="width-full" style="min-width: 0;">
								<Text styleType="body-extra-small" class="text-muted">{row.label}</Text>
								<div class="d-flex align-items-center" style="min-width: 0;">
									<Text styleType="body-small" breakWord>{row.value}</Text>
									<IconButton
										class="ml-s"
										icon="fas fa-copy"
										on:click={() => onCopyValue(row.value, row.label)}
										aria-label={$format('button_copy_to_clipboard', {
											default: 'Copy to clipboard',
										})}
									/>
								</div>
							</div>
						{/each}
					</div>
				{/if}
			</div>
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			variant="secondary"
			on:click={() => (showKeyModal = false)}
			aria-label={$format('button_close', { default: 'Close' })}
		>
			{$format('button_close', { default: 'Close' })}
		</ButtonV2>
	</div>
</Modal>
